/**
 * Helper functions for Level Details Page
 * These utilities handle date formatting, image management, UUID detection, and error handling
 */

/**
 * Format a date string into a readable format
 * @param {string} dateString - ISO 8601 date string
 * @param {string} locale - Locale for date formatting (default: 'en-US')
 * @returns {string} Formatted date string or 'N/A' if invalid
 */
export const formatDate = (dateString, locale = 'en-US') => {
  // Handle null, undefined, or empty string
  if (!dateString) {
    return 'N/A';
  }

  try {
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date string: ${dateString}`);
      return 'N/A';
    }

    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'N/A';
  }
};

/**
 * Get level image URL with fallback logic
 * @param {Object} levelData - Level data object from API
 * @returns {string} Image URL or fallback image path
 */
export const getLevelImage = (levelData) => {
  // Check if levelData exists and has image_url
  if (levelData && levelData.image_url) {
    // Validate that image_url is a non-empty string
    if (typeof levelData.image_url === 'string' && levelData.image_url.trim() !== '') {
      return levelData.image_url;
    }
  }

  // Return fallback image
  return '/images/levels/PHOTO-N0-2048x2048.jpg';
};

/**
 * Detect if a string is a valid UUID (v4)
 * @param {string} str - String to test
 * @returns {boolean} True if string is a valid UUID, false otherwise
 */
export const isUUID = (str) => {
  // Handle null, undefined, or non-string values
  if (!str || typeof str !== 'string') {
    return false;
  }

  // UUID v4 regex pattern
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  
  return uuidRegex.test(str);
};

/**
 * Handle image load errors with fallback
 * @param {Event} event - Image error event
 * @param {string} fallbackUrl - Fallback image URL (default: default level image)
 */
export const handleImageError = (event, fallbackUrl = '/images/levels/PHOTO-N0-2048x2048.jpg') => {
  const img = event.target;
  
  // Prevent infinite loop if fallback image also fails
  if (img.src.includes(fallbackUrl)) {
    img.onerror = null;
    console.error('Fallback image also failed to load');
    return;
  }

  // Set fallback image
  img.src = fallbackUrl;
};

/**
 * Generate responsive image srcset for better performance
 * @param {string} imageUrl - Base image URL
 * @param {Array<number>} widths - Array of widths for srcset (default: [300, 600, 900])
 * @returns {string} srcset string or empty string if no URL provided
 */
export const getImageSrcSet = (imageUrl, widths = [300, 600, 900]) => {
  if (!imageUrl || typeof imageUrl !== 'string') {
    return '';
  }

  // Generate srcset entries for each width
  return widths.map(width => `${imageUrl} ${width}w`).join(', ');
};

/**
 * Get appropriate sizes attribute for responsive images
 * @returns {string} sizes attribute value
 */
export const getImageSizes = () => {
  return '(max-width: 374px) 180px, (max-width: 767px) 200px, (max-width: 1199px) 250px, 300px';
};

/**
 * Parse API error and return user-friendly message
 * @param {Error} error - Error object from API call
 * @returns {Object} Object with error message and status code
 */
export const parseApiError = (error) => {
  // Default error response
  const errorResponse = {
    message: 'An unexpected error occurred. Please try again later.',
    statusCode: null,
    isNotFound: false
  };

  // Handle network errors
  if (!error.response) {
    errorResponse.message = 'Network error. Please check your connection and try again.';
    return errorResponse;
  }

  // Extract status code
  errorResponse.statusCode = error.response.status;

  // Handle specific status codes
  switch (error.response.status) {
    case 404:
      errorResponse.message = 'Level not found';
      errorResponse.isNotFound = true;
      break;
    case 401:
      errorResponse.message = 'Authentication required. Please log in again.';
      break;
    case 403:
      errorResponse.message = 'You do not have permission to access this level.';
      break;
    case 500:
      errorResponse.message = 'Server error. Please try again later.';
      break;
    case 503:
      errorResponse.message = 'Service temporarily unavailable. Please try again later.';
      break;
    default:
      // Use error message from API if available
      if (error.response.data && error.response.data.message) {
        errorResponse.message = error.response.data.message;
      } else {
        errorResponse.message = `Failed to load level details (Error ${error.response.status})`;
      }
  }

  return errorResponse;
};

/**
 * Validate level data object
 * @param {Object} levelData - Level data from API
 * @returns {boolean} True if level data is valid, false otherwise
 */
export const isValidLevelData = (levelData) => {
  if (!levelData || typeof levelData !== 'object') {
    return false;
  }

  // Check for required fields
  const requiredFields = ['id', 'level', 'title'];
  
  for (const field of requiredFields) {
    if (!levelData[field]) {
      console.warn(`Missing required field: ${field}`);
      return false;
    }
  }

  return true;
};

/**
 * Safely get numeric value with fallback to 0
 * @param {*} value - Value to parse as number
 * @returns {number} Parsed number or 0 if invalid
 */
export const safeNumber = (value) => {
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? 0 : parsed;
};
